import { useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Quote } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useFavorites } from '@/hooks/useFavorites';
import { QuoteActions } from '@/components/quote/QuoteActions';
import { QuoteCard } from '@/components/quote/QuoteCard';
import { QuoteSkeleton, Skeleton } from '@/components/ui/Skeleton';
import { downloadQuoteAsImage } from '@/utils/download';

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
};

export function QuoteDetail() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading } = useQuery<{ success: boolean; data: any }>({
    queryKey: ['quote', id],
    queryFn: () => api.quotes.getById(Number(id)),
    enabled: !!id,
  });
  const { data: favData } = useFavorites();
  const downloadRef = useRef<HTMLDivElement>(null);

  const quote = data?.data;
  const favorites = new Set((favData?.data || []).map((f: any) => f.quote_id || f.id));

  const { data: relatedData } = useQuery<{ success: boolean; data: any[] }>({
    queryKey: ['quotes', 'related', quote?.category_slug],
    queryFn: () => api.quotes.getAll({ category: quote?.category_slug || '', limit: '4' }),
    enabled: !!quote?.category_slug,
  });
  const relatedQuotes = (relatedData?.data || []).filter((q: any) => q.id !== quote?.id).slice(0, 3);

  const handleDownload = async () => {
    if (downloadRef.current) {
      await downloadQuoteAsImage(downloadRef.current, `quote-${quote?.id || Date.now()}`, 'png');
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Skeleton className="h-8 w-32 mb-8" />
        <QuoteSkeleton />
      </div>
    );
  }

  if (!quote) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <p className="text-xl text-surface-500">Quote not found</p>
        <Link to="/" className="btn-primary mt-4 inline-flex">Go Home</Link>
      </div>
    );
  }

  return (
    <motion.div initial="initial" animate="animate" className="max-w-4xl mx-auto px-4 py-12">
      <motion.div variants={fadeUp}>
        <Link to="/browse" className="inline-flex items-center gap-2 text-sm text-surface-500 hover:text-surface-900 transition-colors mb-8">
          <ArrowLeft size={16} />
          Back to Browse
        </Link>
      </motion.div>

      <motion.div variants={fadeUp} className="mb-12">
        <div ref={downloadRef} className="glass-card p-8 md:p-12 relative overflow-hidden">
          <Quote size={48} className="absolute top-6 left-6 text-primary-200/30" />
          <p className="text-2xl md:text-3xl leading-relaxed mb-8 relative z-10 pl-4">
            {quote.text}
          </p>

          <div className="flex items-center gap-4 mb-6 relative z-10 pl-4">
            {quote.author_name && (
              <div className="w-12 h-12 rounded-full gradient-bg flex items-center justify-center text-white font-bold text-lg shrink-0">
                {quote.author_name?.charAt(0)}
              </div>
            )}
            <div>
              {quote.author_name && (
                <Link to={`/author/${quote.author_slug}`} className="font-semibold text-lg hover:text-primary-500 transition-colors">
                  {quote.author_name}
                </Link>
              )}
              {quote.category_name && (
                <Link to={`/category/${quote.category_slug}`} className="block text-sm text-surface-500 hover:text-primary-500 transition-colors">
                  {quote.category_name}
                </Link>
              )}
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-center">
          <QuoteActions
            quoteId={quote.id}
            text={quote.text}
            author={quote.author_name || ''}
            isFavorited={favorites.has(quote.id)}
            onDownload={handleDownload}
            layout="vertical"
          />
        </div>
      </motion.div>

      {/* Author Bio */}
      {quote.author_name && (
        <motion.div variants={fadeUp} className="glass-card p-6 mb-12">
          <h3 className="text-lg font-bold mb-2">About {quote.author_name}</h3>
          <Link to={`/author/${quote.author_slug}`} className="text-primary-500 hover:text-primary-600 text-sm transition-colors">
            View all quotes by {quote.author_name} →
          </Link>
        </motion.div>
      )}

      {/* Related Quotes */}
      {relatedQuotes.length > 0 && (
        <motion.div variants={fadeUp}>
          <h3 className="text-xl font-bold mb-6">Related Quotes</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {relatedQuotes.map((q: any) => (
              <Link key={q.id} to={`/quote/${q.id}`}>
                <QuoteCard quote={q} isFavorited={favorites.has(q.id)} />
              </Link>
            ))}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
